import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  RagDocument,
  RagStorageUsage,
  fetchOrgDocuments,
  fetchStorageUsage,
  uploadOrgDocument,
  deleteDocument as apiDeleteDocument,
  retryDocument as apiRetryDocument,
  assignDocumentToAgent as apiAssign,
  unassignDocumentFromAgent as apiUnassign,
} from "../api/ragApi";
import { invalidateAgents } from "../state/ragSlice";

const POLL_INTERVAL = 5000;

export function useOrgDocuments() {
  const dispatch = useDispatch();
  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [storage, setStorage] = useState<RagStorageUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const docs = await fetchOrgDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error("Failed to load org documents:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStorage = useCallback(async () => {
    try {
      setStorage(await fetchStorageUsage());
    } catch (err) {
      console.error("Failed to load storage usage:", err);
    }
  }, []);

  useEffect(() => {
    load();
    loadStorage();
  }, [load, loadStorage]);

  // Poll while any document is processing
  useEffect(() => {
    const hasProcessing = documents.some((d) => d.status === "processing");

    if (hasProcessing) {
      pollRef.current = setInterval(async () => {
        try {
          const docs = await fetchOrgDocuments();
          // MERGE, never replace: keep optimistic placeholders (negative ids)
          // of in-flight uploads or a poll response wipes them and the panel
          // goes permanently blank — same race as useRagDocuments, see
          // playwright/local-only/agent-docs-vanish-repro.spec.ts.
          setDocuments((prev) => [...prev.filter((d) => d.id < 0), ...docs]);
          if (!docs.some((d) => d.status === "processing") && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            // Processing finished → agent chunk counts changed; refresh pickers.
            dispatch(invalidateAgents());
          }
        } catch {
          // Silently fail polling
        }
      }, POLL_INTERVAL);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [documents, dispatch]);

  const tempIdRef = useRef(-1);

  const upload = useCallback(
    async (file: File) => {
      // Optimistic: show placeholder immediately
      const tempId = tempIdRef.current--;
      const placeholder: RagDocument = {
        id: tempId,
        agent_id: null,
        organization_id: 0,
        uploaded_by: 0,
        filename: file.name,
        s3_key: "",
        mime_type: file.type,
        size_bytes: file.size,
        chunk_count: 0,
        total_chunks_expected: 0,
        status: "processing",
        error_message: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        agents: [],
      };
      setDocuments((prev) => [placeholder, ...prev]);
      setUploading(true);

      try {
        const doc = await uploadOrgDocument(file);
        // Replace the placeholder — or PREPEND if a concurrent poll response
        // already removed it, so the real document is never silently dropped.
        setDocuments((prev) =>
          prev.some((d) => d.id === tempId)
            ? prev.map((d) => (d.id === tempId ? doc : d))
            : [doc, ...prev.filter((d) => d.id !== doc.id)],
        );
        dispatch(invalidateAgents());
        loadStorage();
        // Callers use the returned doc to surface server-side dedup
        // (doc.deduplicated) instead of miming a fresh upload.
        return doc;
      } catch (err) {
        // Remove placeholder on failure
        setDocuments((prev) => prev.filter((d) => d.id !== tempId));
        console.error("Failed to upload document:", err);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [dispatch, loadStorage],
  );

  const deleteDoc = useCallback(
    async (docId: number) => {
      try {
        await apiDeleteDocument(docId);
        setDocuments((prev) => prev.filter((d) => d.id !== docId));
        dispatch(invalidateAgents());
        loadStorage();
      } catch (err) {
        console.error("Failed to delete document:", err);
        throw err;
      }
    },
    [dispatch, loadStorage],
  );

  const retryDoc = useCallback(
    async (docId: number) => {
      try {
        const doc = await apiRetryDocument(docId);
        setDocuments((prev) => prev.map((d) => (d.id === docId ? doc : d)));
        dispatch(invalidateAgents());
      } catch (err) {
        console.error("Failed to retry document:", err);
        throw err;
      }
    },
    [dispatch],
  );

  const assignToAgent = useCallback(
    async (docId: number, agentId: number) => {
      await apiAssign(docId, agentId);
      await load();
      dispatch(invalidateAgents());
    },
    [load, dispatch],
  );

  const unassignFromAgent = useCallback(
    async (docId: number, agentId: number) => {
      await apiUnassign(docId, agentId);
      await load();
      dispatch(invalidateAgents());
    },
    [load, dispatch],
  );

  return {
    documents,
    storage,
    loading,
    uploading,
    upload,
    deleteDoc,
    retryDoc,
    assignToAgent,
    unassignFromAgent,
    reload: load,
  };
}
